import AltRoute from '@mui/icons-material/AltRoute';
import GppMaybe from '@mui/icons-material/GppMaybe';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { t } from 'i18next';

import { AiJobSummary, AiLoadSummary } from '~hooks/useAiSummaries/models';

import { headerCellSx } from '../Geofence/geofenceTableStyles';
import { stripedRowSx } from '../Geofence/jobEventsTableShared';
import { formatAiSummaryMinutes, formatAiSummaryTimestamp } from './aiSummaryFormatters';

interface AiSummaryDetailsProps {
  aiJobSummary: AiJobSummary | null;
  aiLoadSummaries: AiLoadSummary[];
}

// 18px matches the severity icons in the findings list
const flagIconSx = { fontSize: 18 };

export function AiSummaryDetails({
  aiJobSummary,
  aiLoadSummaries,
}: AiSummaryDetailsProps) {
  if (!aiJobSummary) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {aiLoadSummaries.length > 0 && (
        <TableContainer
          component={Paper}
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            fontFamily: 'monospace',
            '& .MuiTableCell-root': {
              fontFamily: 'inherit',
              paddingTop: 1,
              paddingBottom: 1,
              lineHeight: 1.2,
              fontSize: '0.8125rem',
              whiteSpace: 'nowrap',
            },
            '& .MuiTableRow-root': {
              '& td, & th': { borderBottomWidth: 1 },
            },
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>
                  {t('admin.job_geo_debug.ai_summary_col_load')}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  {t('admin.job_geo_debug.ai_summary_col_pickup_in')}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  {t('admin.job_geo_debug.ai_summary_col_pickup_out')}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  {t('admin.job_geo_debug.ai_summary_col_dropoff_in')}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  {t('admin.job_geo_debug.ai_summary_col_dropoff_out')}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  {t('admin.job_geo_debug.ai_summary_col_loading')}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  {t('admin.job_geo_debug.ai_summary_col_unloading')}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  {t('admin.job_geo_debug.ai_summary_col_cycle')}
                </TableCell>
                <TableCell sx={headerCellSx}>
                  {t('admin.job_geo_debug.ai_summary_col_flags')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {aiLoadSummaries.map((aiLoadSummary, index) => (
                <TableRow
                  key={aiLoadSummary.id}
                  data-test-id="ai-load-summary-row"
                  sx={stripedRowSx(index)}
                >
                  <TableCell>{aiLoadSummary.loadIndex + 1}</TableCell>
                  <TableCell>
                    {formatAiSummaryTimestamp(aiLoadSummary.pickupEnteredAt)}
                  </TableCell>
                  <TableCell>
                    {formatAiSummaryTimestamp(aiLoadSummary.pickupExitedAt)}
                  </TableCell>
                  <TableCell>
                    {formatAiSummaryTimestamp(aiLoadSummary.dropoffEnteredAt)}
                  </TableCell>
                  <TableCell>
                    {formatAiSummaryTimestamp(aiLoadSummary.dropoffExitedAt)}
                  </TableCell>
                  <TableCell>
                    {formatAiSummaryMinutes(aiLoadSummary.loadingDurationSeconds)}
                  </TableCell>
                  <TableCell>
                    {formatAiSummaryMinutes(aiLoadSummary.unloadingDurationSeconds)}
                  </TableCell>
                  <TableCell>
                    {formatAiSummaryMinutes(aiLoadSummary.cycleDurationSeconds)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
                      {aiLoadSummary.siteChange && (
                        <Tooltip
                          title={t('admin.job_geo_debug.ai_summary_site_change_tooltip')}
                        >
                          <AltRoute sx={{ ...flagIconSx, color: 'info.main' }} />
                        </Tooltip>
                      )}
                      {!aiLoadSummary.validated && (
                        <Tooltip
                          title={t(
                            'admin.job_geo_debug.ai_summary_not_validated_tooltip',
                          )}
                        >
                          <GppMaybe sx={{ ...flagIconSx, color: 'warning.main' }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
